import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import type { TooltipProps } from "recharts";
import { formatNumber } from "@/features/reports/components/chartTheme";

/**
 * Formats a single series value for display. Defaults to the shared
 * `formatNumber` helper; callers pass a custom formatter to control units
 * (км / ч / % / €) and locale.
 */
export type ChartValueFormatter = (value: ValueType, name: NameType) => string;

interface Props extends TooltipProps<ValueType, NameType> {
  /**
   * Per-value formatter. Receives the raw value + series name and returns the
   * display string. Defaults to plain `formatNumber` (no unit, no locale).
   */
  valueFormatter?: ChartValueFormatter;
  /** Optional formatter for the bold label/header row. */
  labelFormatter?: (label: unknown) => string;
}

function defaultValueFormatter(value: ValueType): string {
  return formatNumber(typeof value === "number" ? value : Number(value));
}

/**
 * Shared Recharts tooltip for every Analytics chart (TASK-3835).
 *
 * Rendered as a rounded card with a bold label row and one row per series:
 * colored dot + series name + formatted value. Tailwind-only; the single inline
 * style is the dynamic dot color (the standard justified exception — Tailwind
 * can't express a runtime hex). Generic enough for both bar and line charts.
 */
export function ChartTooltip({
  active,
  payload,
  label,
  valueFormatter = defaultValueFormatter,
  labelFormatter,
}: Props) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const headerLabel = labelFormatter
    ? labelFormatter(label)
    : String(label ?? "");

  return (
    <div className="rounded-xl border border-gray-100 bg-white px-3 py-2 shadow-lg">
      {headerLabel ? (
        <p className="mb-1 text-xs font-bold text-gray-900">{headerLabel}</p>
      ) : null}
      <ul className="space-y-0.5">
        {payload.map((entry, index) => (
          <li
            key={`${entry.dataKey ?? entry.name ?? index}`}
            className="flex items-center gap-2 text-xs text-gray-600"
          >
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: entry.color }}
              aria-hidden="true"
            />
            <span className="flex-1 truncate">{entry.name}</span>
            <span className="font-semibold tabular-nums text-gray-900">
              {valueFormatter(entry.value as ValueType, entry.name as NameType)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
